import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import EmptyData from '../../../components/UI/EmptyData';
import {
  getViolationEmployeeName,
  getViolationEmployeeSubtext,
  getViolationRowId,
  getViolationSummaries,
  getViolationTypeLabel,
  getWarningStatusConfig
} from '../../../utils/attendanceViolationHelpers';

import ViolationUserDetailModal from './ViolationUserDetailModal';
import { ViolationWidgetSkeleton } from '../../Admin/AttendanceViolation/components/ViolationSkeletons';

import './AttendanceViolationWidget.scss';

const TypeChip = ({ violationType, count }) => {
  const countNum = Number(count);
  const showCount = Number.isFinite(countNum) && countNum > 0;

  return (
    <span
      className={`att-violation-widget__type att-violation-widget__type--${
        violationType === 'EARLY_EXIT' ? 'exit' : 'late'
      }`}
    >
      {getViolationTypeLabel(violationType)}
      {showCount ? <span className="att-violation-widget__type-count">{countNum}</span> : null}
    </span>
  );
};

const ViolationRow = ({ row, onSelect }) => {
  const summaries = getViolationSummaries(row);
  const statusConfig = getWarningStatusConfig(row?.warningStatus || row?.status);
  const profilePicture = row?.user?.profilePicture;
  const totalViolations = row?.totalViolationsThisMonth ?? row?.monthlyViolationCount ?? 0;
  const isGrouped = summaries.length > 0 && row?.user;

  const handleClick = () => {
    if (isGrouped) onSelect?.(row);
  };

  const handleKeyDown = (e) => {
    if (!isGrouped) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect?.(row);
    }
  };

  if (!isGrouped) {
    return (
      <li className="att-violation-widget__row">
        <div className="att-violation-widget__main">
          <p className="att-violation-widget__name">{getViolationEmployeeName(row)}</p>
          <p className="att-violation-widget__meta">{getViolationTypeLabel(row?.violationType)}</p>
        </div>
        <span
          className={`att-violation-widget__status att-violation-widget__status--${statusConfig.color}`}
        >
          {statusConfig.label}
        </span>
      </li>
    );
  }

  return (
    <li
      className="att-violation-widget__row att-violation-widget__row--clickable"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`View violations for ${getViolationEmployeeName(row)}`}
    >
      <div className="att-violation-widget__user">
        <div className="att-violation-widget__avatar-wrap">
          {profilePicture ? (
            <img className="att-violation-widget__avatar" src={profilePicture} alt="" />
          ) : (
            <span className="att-violation-widget__avatar att-violation-widget__avatar--placeholder">
              {(getViolationEmployeeName(row) || '?').slice(0, 1).toUpperCase()}
            </span>
          )}
          {totalViolations > 0 ? (
            <span className="att-violation-widget__avatar-count">{totalViolations}</span>
          ) : null}
        </div>
        <div className="att-violation-widget__main">
          <p className="att-violation-widget__name">{getViolationEmployeeName(row)}</p>
          <div className="att-violation-widget__subline">
            <p className="att-violation-widget__role">{getViolationEmployeeSubtext(row)}</p>
            <div className="att-violation-widget__types">
              {summaries.map((summary) => (
                <TypeChip
                  key={summary.violationType}
                  violationType={summary.violationType}
                  count={summary?.monthlyViolationCount}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <span className="att-violation-widget__chevron-btn" aria-hidden>
        <i className="bi bi-chevron-right att-violation-widget__chevron" />
      </span>
    </li>
  );
};

const AttendanceViolationWidget = ({ summary, onRefresh, loading = false }) => {
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState(null);
  const totalCount = Number(summary?.totalCount) || 0;
  const topViolations = summary?.topViolations || [];
  const showSkeleton = loading;

  return (
    <>
      <div className="card att-violation-widget h-100">
        {showSkeleton ? (
          <div className="att-violation-widget__head att-violation-widget__head--skeleton">
            <div>
              <span className="att-violation-skeleton__bar att-violation-skeleton__bar--title" />
              <span className="att-violation-skeleton__bar att-violation-skeleton__bar--subtitle" />
            </div>
            <span className="att-violation-skeleton__bar att-violation-skeleton__bar--count" />
          </div>
        ) : (
          <div className="att-violation-widget__head">
            <div className="att-violation-widget__title-block">
              <h2 className="att-violation-widget__title">Attendance Violations</h2>
              <p className="att-violation-widget__subtitle">Monthly late arrival & early exit</p>
            </div>
            {totalCount > 0 ? (
              <span className="att-violation-widget__count">{totalCount}</span>
            ) : null}
          </div>
        )}

        <div className="att-violation-widget__body">
          {showSkeleton ? (
            <ViolationWidgetSkeleton rows={4} />
          ) : topViolations.length ? (
            <ul className="att-violation-widget__list">
              {topViolations.map((row, idx) => (
                <ViolationRow
                  key={getViolationRowId(row) || `violation-${idx}`}
                  row={row}
                  onSelect={setSelectedUser}
                />
              ))}
            </ul>
          ) : (
            <div className="att-violation-widget__empty">
              <EmptyData />
            </div>
          )}
        </div>

        {!showSkeleton && topViolations.length > 0 ? (
          <div className="att-violation-widget__foot">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => navigate('/attendance-violations')}
            >
              View all{totalCount > 5 ? ` (${totalCount})` : ''} violations
            </button>
          </div>
        ) : null}
      </div>

      <ViolationUserDetailModal
        show={Boolean(selectedUser)}
        userRow={selectedUser}
        onClose={() => setSelectedUser(null)}
        onLeaveApplied={() => {
          onRefresh?.();
          setSelectedUser(null);
        }}
      />
    </>
  );
};

export default AttendanceViolationWidget;
